import { type FC, type ReactNode } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useReports } from '@/hooks/useReports';

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const numberFormatter = new Intl.NumberFormat('pt-BR');

const formatCurrency = (value: number) => currencyFormatter.format(Number.isFinite(value) ? value : 0);

const formatNumber = (value: number) => numberFormatter.format(Number.isFinite(value) ? value : 0);

const formatDateTime = (value: string | null | undefined) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className="rounded-2xl border border-background-200 bg-background-50 shadow-sm">
    <div className="border-b border-background-100 px-4 py-3">
      <h3 className="text-sm font-semibold text-foreground-800">{title}</h3>
    </div>
    <div className="p-4">{children}</div>
  </section>
);

const ReportsPage: FC = () => {
  const { reports, isLoadingReports, reportsError, refreshReports } = useReports();
  const summaryCards = [
    { label: 'Clientes', value: reports.summary.totalClients, icon: 'ri-group-line' },
    { label: 'Leads', value: reports.summary.totalLeads, icon: 'ri-contacts-line' },
    { label: 'Casos', value: reports.summary.totalCases, icon: 'ri-briefcase-line' },
    { label: 'Tarefas', value: reports.summary.totalTasks, icon: 'ri-task-line' },
    { label: 'Documentos gerados', value: reports.summary.totalGeneratedDocuments, icon: 'ri-file-text-line' },
    { label: 'Agenda', value: reports.summary.totalCalendarEvents, icon: 'ri-calendar-line' },
    { label: 'Produtos jurídicos', value: reports.summary.totalLegalProducts, icon: 'ri-archive-line' },
    { label: 'Sites jurídicos', value: reports.summary.totalLegalSites, icon: 'ri-links-line' },
  ];

  const hasLeads = reports.leads.byStatus.length > 0 || reports.leads.bySource.length > 0 || reports.leads.recent.length > 0;
  const hasCases = reports.cases.byStatus.length > 0 || reports.cases.byArea.length > 0 || reports.cases.byPhase.length > 0 || reports.cases.recent.length > 0;
  const hasTasks = reports.tasks.byStatus.length > 0 || reports.tasks.recent.length > 0;
  const hasFinance =
    reports.finance.byStatus.length > 0 ||
    reports.finance.recentPayments.length > 0 ||
    reports.finance.overdueInstallments.length > 0 ||
    reports.finance.upcomingInstallments.length > 0;
  const hasProductivity =
    reports.productivity.documentTemplates > 0 ||
    reports.productivity.generatedDocuments > 0 ||
    reports.productivity.messageTemplates > 0 ||
    reports.productivity.knowledgeItems > 0;

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="space-y-1">
            <h2 className="text-xl font-bold text-foreground-900">Relatórios</h2>
            <p className="max-w-3xl text-sm text-foreground-500">
              Relatórios gerados com base nos dados cadastrados no sistema. Indicadores podem variar conforme o
              preenchimento dos módulos.
            </p>
          </div>

          <button
            type="button"
            onClick={refreshReports}
            disabled={isLoadingReports}
            className="inline-flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <i className={`ri-refresh-line ${isLoadingReports ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>

        {reportsError ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {reportsError}
          </div>
        ) : null}

        {isLoadingReports ? (
          <div className="rounded-2xl border border-background-200 bg-background-50 px-4 py-10 text-center text-sm text-foreground-500">
            Carregando relatórios reais...
          </div>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map((item) => (
            <div key={item.label} className="rounded-2xl border border-background-200 bg-background-50 p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-foreground-500">{item.label}</p>
                  <p className="mt-2 text-2xl font-bold text-foreground-900">{formatNumber(item.value)}</p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                  <i className={`${item.icon} text-lg`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <SectionCard title="Leads">
            {hasLeads ? (
              <div className="space-y-4">
                <div className="grid gap-3 md:grid-cols-3">
                  {reports.leads.byStatus.map((item) => (
                    <div key={`lead-status-${item.label}`} className="rounded-xl border border-background-200 bg-background-50 p-3">
                      <p className="text-xs text-foreground-500">Status</p>
                      <p className="text-sm font-semibold text-foreground-800">{item.label}</p>
                      <p className="text-lg font-bold text-foreground-900">{formatNumber(item.count)}</p>
                    </div>
                  ))}
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-xl border border-background-200">
                    <div className="border-b border-background-100 px-3 py-2 text-sm font-semibold text-foreground-700">
                      Por origem
                    </div>
                    <ul className="divide-y divide-background-100">
                      {reports.leads.bySource.length > 0 ? (
                        reports.leads.bySource.map((item) => (
                          <li key={`lead-source-${item.label}`} className="flex items-center justify-between px-3 py-2 text-sm">
                            <span className="text-foreground-700">{item.label}</span>
                            <span className="font-medium text-foreground-900">{formatNumber(item.count)}</span>
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-3 text-sm text-foreground-500">Sem registros por origem.</li>
                      )}
                    </ul>
                  </div>

                  <div className="rounded-xl border border-background-200">
                    <div className="border-b border-background-100 px-3 py-2 text-sm font-semibold text-foreground-700">
                      Recentes
                    </div>
                    <ul className="divide-y divide-background-100">
                      {reports.leads.recent.length > 0 ? (
                        reports.leads.recent.map((item) => (
                          <li key={item.id} className="px-3 py-2">
                            <p className="text-sm font-medium text-foreground-800">{item.name}</p>
                            <p className="text-xs text-foreground-500">
                              {item.status} · {item.priority} · {formatDateTime(item.createdAt)}
                            </p>
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-3 text-sm text-foreground-500">Nenhum lead recente encontrado.</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-foreground-500">Nenhum lead cadastrado ainda.</p>
            )}
          </SectionCard>

          <SectionCard title="Casos / Processos">
            {hasCases ? (
              <div className="space-y-4">
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                    <p className="text-xs text-foreground-500">Ativos</p>
                    <p className="text-lg font-bold text-foreground-900">{formatNumber(reports.cases.activeCount)}</p>
                  </div>
                  <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                    <p className="text-xs text-foreground-500">Arquivados</p>
                    <p className="text-lg font-bold text-foreground-900">{formatNumber(reports.cases.archivedCount)}</p>
                  </div>
                  <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                    <p className="text-xs text-foreground-500">Total recente</p>
                    <p className="text-lg font-bold text-foreground-900">{formatNumber(reports.cases.recent.length)}</p>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-xl border border-background-200">
                    <div className="border-b border-background-100 px-3 py-2 text-sm font-semibold text-foreground-700">
                      Por área
                    </div>
                    <ul className="divide-y divide-background-100">
                      {reports.cases.byArea.length > 0 ? (
                        reports.cases.byArea.map((item) => (
                          <li key={`case-area-${item.label}`} className="flex items-center justify-between px-3 py-2 text-sm">
                            <span className="text-foreground-700">{item.label}</span>
                            <span className="font-medium text-foreground-900">{formatNumber(item.count)}</span>
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-3 text-sm text-foreground-500">Sem casos por área.</li>
                      )}
                    </ul>
                  </div>

                  <div className="rounded-xl border border-background-200">
                    <div className="border-b border-background-100 px-3 py-2 text-sm font-semibold text-foreground-700">
                      Próximos prazos
                    </div>
                    <ul className="divide-y divide-background-100">
                      {reports.cases.recent.length > 0 ? (
                        reports.cases.recent.map((item) => (
                          <li key={item.id} className="px-3 py-2">
                            <p className="text-sm font-medium text-foreground-800">{item.title}</p>
                            <p className="text-xs text-foreground-500">
                              {item.clientName || 'Cliente não informado'} · {item.phase || 'Sem fase'} ·{' '}
                              {item.nextDeadlineAt ? formatDateTime(item.nextDeadlineAt) : 'Sem prazo'}
                            </p>
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-3 text-sm text-foreground-500">Nenhum caso recente encontrado.</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-foreground-500">Nenhum caso cadastrado ainda.</p>
            )}
          </SectionCard>
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <SectionCard title="Tarefas">
            {hasTasks ? (
              <div className="space-y-4">
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                    <p className="text-xs text-foreground-500">Concluídas</p>
                    <p className="text-lg font-bold text-foreground-900">{formatNumber(reports.tasks.completedCount)}</p>
                  </div>
                  <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                    <p className="text-xs text-foreground-500">Vencidas</p>
                    <p className="text-lg font-bold text-foreground-900">{formatNumber(reports.tasks.overdueCount)}</p>
                  </div>
                  <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                    <p className="text-xs text-foreground-500">Próximos 7 dias</p>
                    <p className="text-lg font-bold text-foreground-900">{formatNumber(reports.tasks.upcoming7DaysCount)}</p>
                  </div>
                </div>

                <div className="rounded-xl border border-background-200">
                  <div className="border-b border-background-100 px-3 py-2 text-sm font-semibold text-foreground-700">
                    Por status
                  </div>
                  <ul className="divide-y divide-background-100">
                    {reports.tasks.byStatus.length > 0 ? (
                      reports.tasks.byStatus.map((item) => (
                        <li key={`task-status-${item.label}`} className="flex items-center justify-between px-3 py-2 text-sm">
                          <span className="text-foreground-700">{item.label}</span>
                          <span className="font-medium text-foreground-900">{formatNumber(item.count)}</span>
                        </li>
                      ))
                    ) : (
                      <li className="px-3 py-3 text-sm text-foreground-500">Sem tarefas por status.</li>
                    )}
                  </ul>
                </div>

                <div className="rounded-xl border border-background-200">
                  <div className="border-b border-background-100 px-3 py-2 text-sm font-semibold text-foreground-700">
                    Recentes
                  </div>
                  <ul className="divide-y divide-background-100">
                    {reports.tasks.recent.length > 0 ? (
                      reports.tasks.recent.map((item) => (
                        <li key={item.id} className="px-3 py-2">
                          <p className="text-sm font-medium text-foreground-800">{item.title}</p>
                          <p className="text-xs text-foreground-500">
                            {item.status} · {item.priority} · {item.clientName || 'Sem cliente'} ·{' '}
                            {item.dueDate ? formatDateTime(item.dueDate) : 'Sem vencimento'}
                          </p>
                        </li>
                      ))
                    ) : (
                      <li className="px-3 py-3 text-sm text-foreground-500">Nenhuma tarefa recente encontrada.</li>
                    )}
                  </ul>
                </div>
              </div>
            ) : (
              <p className="text-sm text-foreground-500">Nenhuma tarefa cadastrada ainda.</p>
            )}
          </SectionCard>

          <SectionCard title="Financeiro básico">
            {hasFinance ? (
              <div className="space-y-4">
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                    <p className="text-xs text-foreground-500">Previsto</p>
                    <p className="text-lg font-bold text-foreground-900">{formatCurrency(reports.finance.totalContractedRevenue)}</p>
                  </div>
                  <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                    <p className="text-xs text-foreground-500">Recebido</p>
                    <p className="text-lg font-bold text-foreground-900">{formatCurrency(reports.finance.totalReceivedRevenue)}</p>
                  </div>
                  <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                    <p className="text-xs text-foreground-500">A receber</p>
                    <p className="text-lg font-bold text-foreground-900">{formatCurrency(reports.finance.totalPendingRevenue)}</p>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-xl border border-background-200">
                    <div className="border-b border-background-100 px-3 py-2 text-sm font-semibold text-foreground-700">
                      Status dos pagamentos
                    </div>
                    <ul className="divide-y divide-background-100">
                      {reports.finance.byStatus.length > 0 ? (
                        reports.finance.byStatus.map((item) => (
                          <li key={`payment-status-${item.label}`} className="flex items-center justify-between px-3 py-2 text-sm">
                            <span className="text-foreground-700">{item.label}</span>
                            <span className="font-medium text-foreground-900">{formatNumber(item.count)}</span>
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-3 text-sm text-foreground-500">Sem pagamentos por status.</li>
                      )}
                    </ul>
                  </div>

                  <div className="rounded-xl border border-background-200">
                    <div className="border-b border-background-100 px-3 py-2 text-sm font-semibold text-foreground-700">
                      Parcelas
                    </div>
                    <div className="space-y-2 p-3 text-sm text-foreground-700">
                      <p>Vencidas: {formatNumber(reports.finance.overdueInstallmentsCount)} · {formatCurrency(reports.finance.overdueInstallmentsAmount)}</p>
                      <p>Próximos 30 dias: {formatNumber(reports.finance.upcomingInstallmentsCount)} · {formatCurrency(reports.finance.upcomingInstallmentsAmount)}</p>
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-xl border border-background-200">
                    <div className="border-b border-background-100 px-3 py-2 text-sm font-semibold text-foreground-700">
                      Últimos pagamentos
                    </div>
                    <ul className="divide-y divide-background-100">
                      {reports.finance.recentPayments.length > 0 ? (
                        reports.finance.recentPayments.map((item) => (
                          <li key={item.id} className="px-3 py-2">
                            <p className="text-sm font-medium text-foreground-800">{item.title}</p>
                            <p className="text-xs text-foreground-500">
                              {item.clientName || 'Sem cliente'} · {item.status} · {formatCurrency(item.balanceAmount)}
                            </p>
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-3 text-sm text-foreground-500">Nenhum pagamento recente encontrado.</li>
                      )}
                    </ul>
                  </div>

                  <div className="rounded-xl border border-background-200">
                    <div className="border-b border-background-100 px-3 py-2 text-sm font-semibold text-foreground-700">
                      Próximas parcelas
                    </div>
                    <ul className="divide-y divide-background-100">
                      {reports.finance.upcomingInstallments.length > 0 ? (
                        reports.finance.upcomingInstallments.map((item) => (
                          <li key={item.id} className="px-3 py-2">
                            <p className="text-sm font-medium text-foreground-800">
                              {item.paymentTitle} · Parcela {item.installmentNumber}
                            </p>
                            <p className="text-xs text-foreground-500">
                              {item.clientName || 'Sem cliente'} · {item.dueDate ? formatDateTime(item.dueDate) : 'Sem data'} ·{' '}
                              {formatCurrency(item.amount - item.paidAmount)}
                            </p>
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-3 text-sm text-foreground-500">Nenhuma parcela futura encontrada.</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-foreground-500">Nenhum lançamento financeiro encontrado.</p>
            )}
          </SectionCard>
        </div>

        <SectionCard title="Produtividade e estrutura">
          {hasProductivity ? (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
              <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                <p className="text-xs text-foreground-500">Modelos de documentos</p>
                <p className="mt-1 text-lg font-bold text-foreground-900">{formatNumber(reports.productivity.documentTemplates)}</p>
              </div>
              <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                <p className="text-xs text-foreground-500">Documentos gerados</p>
                <p className="mt-1 text-lg font-bold text-foreground-900">{formatNumber(reports.productivity.generatedDocuments)}</p>
              </div>
              <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                <p className="text-xs text-foreground-500">Mensagens prontas</p>
                <p className="mt-1 text-lg font-bold text-foreground-900">{formatNumber(reports.productivity.messageTemplates)}</p>
              </div>
              <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                <p className="text-xs text-foreground-500">Itens da biblioteca</p>
                <p className="mt-1 text-lg font-bold text-foreground-900">{formatNumber(reports.productivity.knowledgeItems)}</p>
              </div>
              <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                <p className="text-xs text-foreground-500">Sites jurídicos</p>
                <p className="mt-1 text-lg font-bold text-foreground-900">{formatNumber(reports.productivity.legalSites)}</p>
              </div>
              <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                <p className="text-xs text-foreground-500">Eventos da agenda</p>
                <p className="mt-1 text-lg font-bold text-foreground-900">{formatNumber(reports.productivity.calendarEvents)}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-foreground-500">Nenhum item de produtividade encontrado ainda.</p>
          )}
        </SectionCard>

        <div className="rounded-xl border border-dashed border-background-300 bg-background-50 px-4 py-3">
          <p className="text-xs text-foreground-500">
            Relatórios gerados com base nos dados cadastrados no sistema. Indicadores podem variar conforme o
            preenchimento dos módulos.
          </p>
        </div>
      </div>
    </AppLayout>
  );
};

export default ReportsPage;
